import type { InfoBoxColorscheme } from '../../../../components/InfoBox'
import type {
  MigrationStatus,
  WealthManagerClientDto,
} from '../../../../redux/services/coreApi'

// How each rung of the coarse migration ladder reads to the wealth manager, in ladder order. The labels
// are deliberately plain: the suspicion states have already been collapsed server-side into
// `OnboardingInProgress` (tipping-off, ADR-0006 §3), and `Rejected` is a bare verdict.
export const STATUS_PRESENTATION: Record<
  MigrationStatus,
  { label: string; colorScheme: InfoBoxColorscheme }
> = {
  NotActivated: { label: 'Not activated', colorScheme: 'gray' },
  Activated: { label: 'Activated', colorScheme: 'orange' },
  OnboardingInProgress: {
    label: 'Onboarding in progress',
    colorScheme: 'orange',
  },
  Onboarded: { label: 'Onboarded', colorScheme: 'green' },
  Rejected: { label: 'Rejected', colorScheme: 'red' },
}

export const STATUS_ORDER = Object.keys(
  STATUS_PRESENTATION
) as MigrationStatus[]

export type StatusFilter = MigrationStatus | 'All'

// The import only requires an email (ADR-0004), so a client can have no name on file; the email stands in.
export const hasName = (client: WealthManagerClientDto) =>
  client.name.trim().length > 0

export const displayName = (client: WealthManagerClientDto) =>
  hasName(client) ? client.name.trim() : client.email

export const initials = (client: WealthManagerClientDto) =>
  hasName(client)
    ? client.name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() ?? '')
        .join('')
    : '@'

// Case- and accent-insensitive, so "kusar" finds "Aleš Kušar" — the book is largely Slovenian names.
const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

export const matchesSearch = (
  client: WealthManagerClientDto,
  query: string
) => {
  const needle = fold(query.trim())
  if (!needle) return true
  return (
    fold(client.name).includes(needle) || fold(client.email).includes(needle)
  )
}

export const countByStatus = (clients: WealthManagerClientDto[]) => {
  const counts = Object.fromEntries(
    STATUS_ORDER.map(status => [status, 0])
  ) as Record<MigrationStatus, number>
  for (const client of clients) counts[client.status]++
  return counts
}
